import React, { useEffect, useState } from 'react';
import {
  FiChevronDown,
  FiChevronUp,
  FiSlash,
  FiXCircle,
  FiCheckCircle,
} from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';

import { TopBar } from '@/design-system/components/organisms/TopBar';
import { ApproveModal, type ApproveData } from '@/features/onboarding/components/ApproveModal';
import { BlocklistModal, type BlocklistData } from '@/features/onboarding/components/BlocklistModal';
import { RejectModal, type RejectData } from '@/features/onboarding/components/RejectModal';
import { Sidebar } from '@/features/onboarding/components/Sidebar';
import { Loading } from '@/shared/components/Loading';
import { ONBOARDING_SIDEBAR_ITEMS } from '@/shared/constants/sidebar.constants';

interface Partner {
  name: string;
  role: string;
  hasInterpol: boolean;
  hasOnu: boolean;
  isPep: boolean;
  isFederalRegular: boolean;
  passedLiveness: boolean;
  passedFacematch: boolean;
}

export const DimensaPage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [expandedPartners, setExpandedPartners] = useState<Record<number, boolean>>({});
  const [isBlocklistModalOpen, setIsBlocklistModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);

  useEffect(() => {
    // Simula carregamento da API
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => { clearTimeout(timer); };
  }, []);

  const handleBlocklistConfirm = (data: BlocklistData) => {
    // Aqui você faria a chamada API para adicionar à blocklist
    setIsBlocklistModalOpen(false);
  };

  const handleRejectConfirm = (data: RejectData) => {
    // Aqui você faria a chamada API para reprovar a conta
    setIsRejectModalOpen(false);
  };

  const handleApproveConfirm = (data: ApproveData) => {
    // Aqui você faria a chamada API para aprovar a conta
    setIsApproveModalOpen(false);
  };

  const partners: Partner[] = [
    {
      name: 'Alexandre de Oliveira',
      role: 'Admistrador',
      hasInterpol: false,
      hasOnu: false,
      isPep: false,
      isFederalRegular: false,
      passedLiveness: true,
      passedFacematch: true,
    },
    {
      name: 'Alexandre de Oliveira',
      role: '',
      hasInterpol: false,
      hasOnu: false,
      isPep: false,
      isFederalRegular: false,
      passedLiveness: false,
      passedFacematch: false,
    },
  ];

  const togglePartner = (index: number) => {
    setExpandedPartners(prev => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (isLoading) {
    return <Loading fullScreen message="Carregando dados da Dimensa..." />;
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <TopBar />

      <div className="flex">
        <Sidebar items={ONBOARDING_SIDEBAR_ITEMS} className="w-64 min-h-[calc(100vh-140px)]" />

        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-sm text-neutral-600 mb-6">
              <button
                type="button"
                onClick={() => navigate('/onboarding/pre-analysis')}
                className="hover:text-purple-600 transition-colors"
              >
                Pré análise
              </button>
              <span>{'>'}</span>
              <button
                type="button"
                onClick={() => {
                  void navigate(`/onboarding/client/${clientId ?? ''}`);
                }}
                className="hover:text-purple-600 transition-colors"
              >
                Cliente
              </button>
            </div>

            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium">
                Formal
              </span>
              <h1 className="text-3xl font-bold text-neutral-800">FIDUCIA SCM</h1>
            </div>

            {/* Dados Dimensa */}
            <div className="bg-white rounded-lg border border-neutral-200 p-6 mb-6">
              <h2 className="text-lg font-semibold text-neutral-800 mb-6">Dados Dimensa</h2>

              <div className="space-y-4">
                {partners.map((partner, index) => (
                  <div
                    key={index}
                    className="border border-neutral-200 rounded-lg overflow-hidden"
                  >
                    {/* Partner Header */}
                    <button
                      type="button"
                      onClick={() => { togglePartner(index); }}
                      className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-medium text-neutral-800">{partner.name}</span>
                        {partner.role && (
                          <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            {partner.role}
                          </span>
                        )}
                      </div>
                      {expandedPartners[index] ? (
                        <FiChevronUp className="w-5 h-5 text-neutral-600" />
                      ) : (
                        <FiChevronDown className="w-5 h-5 text-neutral-600" />
                      )}
                    </button>

                    {/* Partner Details */}
                    {expandedPartners[index] && (
                      <div className="p-4 bg-neutral-50 border-t border-neutral-200 space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-neutral-600">Tem registros na Interpol?</span>
                          <span className="font-medium text-neutral-800">
                            {partner.hasInterpol ? 'Sim' : 'Não'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-neutral-600">Tem registros na ONU?</span>
                          <span className="font-medium text-neutral-800">
                            {partner.hasOnu ? 'Sim' : 'Não'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-neutral-600">É PEP?</span>
                          <span className="font-medium text-neutral-800">
                            {partner.isPep ? 'Sim' : 'Não'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-neutral-600">Está regular na receita federal?</span>
                          <span className="font-medium text-neutral-800">
                            {partner.isFederalRegular ? 'Sim' : 'Não'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-neutral-600">Passou no Liveness?</span>
                          <span className="font-medium text-neutral-800">
                            {partner.passedLiveness ? 'Sim' : 'Não'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-neutral-600">Deu certo o Facematch?</span>
                          <span className="font-medium text-neutral-800">
                            {partner.passedFacematch ? 'Sim' : 'Não'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => { setIsBlocklistModalOpen(true); }}
                className="flex items-center gap-2 px-6 py-3 border border-red-600 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors"
              >
                <FiSlash className="w-5 h-5" />
                Adicionar a Blacklist
              </button>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => { setIsRejectModalOpen(true); }}
                  className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                >
                  <FiXCircle className="w-5 h-5" />
                  Reprovar conta
                </button>
                <button
                  type="button"
                  onClick={() => { setIsApproveModalOpen(true); }}
                  className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
                >
                  <FiCheckCircle className="w-5 h-5" />
                  Aprovar conta
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Blocklist Modal */}
      <BlocklistModal
        isOpen={isBlocklistModalOpen}
        onClose={() => { setIsBlocklistModalOpen(false); }}
        onConfirm={handleBlocklistConfirm}
        clientName="FIDUCIA SCM"
      />

      {/* Reject Modal */}
      <RejectModal
        isOpen={isRejectModalOpen}
        onClose={() => { setIsRejectModalOpen(false); }}
        onConfirm={handleRejectConfirm}
        clientName="FIDUCIA SCM"
      />

      {/* Approve Modal */}
      <ApproveModal
        isOpen={isApproveModalOpen}
        onClose={() => { setIsApproveModalOpen(false); }}
        onConfirm={handleApproveConfirm}
        clientName="FIDUCIA SCM"
      />
    </div>
  );
};
